/**
 * PremiumActivityFeed.jsx
 * Timeline de eventos/atividades do sistema
 *
 * Features:
 * - Timeline visual com linha vertical
 * - Dots coloridos por tipo de evento
 * - Agrupamento por dia (Hoje, Ontem, data)
 * - Timestamps relativos
 * - Infinite scroll
 * - Hover/click interactions
 * - Dark/Light mode via tokens
 *
 * Componentes:
 * - PremiumActivityFeed (principal)
 * - ActivityEvent
 * - ActivityDaySeparator
 * - ActivityEventIcon
 *
 * Uso:
 *   import { PremiumActivityFeed } from './premium';
 *
 *   <PremiumActivityFeed
 *     events={events}
 *     groupByDay
 *     onEventClick={(event) => console.log(event)}
 *   />
 */

import React, {
 useMemo, useCallback, useRef, useEffect 
} from 'react';
import PropTypes from 'prop-types';
import {
 Box, Typography, Skeleton, Tooltip, CircularProgress 
} from '@mui/material';

// Icons
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StopIcon from '@mui/icons-material/Stop';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';

import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata timestamp relativo
 */
export const formatRelativeTime = (date, t) => {
  const now = new Date();
  const eventDate = new Date(date);
  const diffMs = now - eventDate;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return t('activityJustNow') || 'Agora mesmo';
  }
  if (diffMinutes < 60) {
    const text = t('activityMinutesAgo') || 'Há {count} minutos';
    return text.replace('{count}', diffMinutes);
  }
  if (diffHours < 24) {
    const text = t('activityHoursAgo') || 'Há {count} horas';
    return text.replace('{count}', diffHours);
  }
  if (diffDays === 1) {
    return t('activityYesterday') || 'Ontem';
  }

  // Formato de data
  return eventDate.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Verifica se duas datas são do mesmo dia
 */
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

/**
 * Verifica se a data é hoje
 */
const isToday = (date) => isSameDay(date, new Date());

/**
 * Verifica se a data é ontem
 */
const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
};

/**
 * Formata label do dia
 */
const formatDayLabel = (date, t) => {
  if (isToday(date)) return t('activityToday') || 'Hoje';
  if (isYesterday(date)) return t('activityYesterday') || 'Ontem';

  return new Date(date).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Obtém configuração do tipo de evento
 */
export const getEventConfig = (type, tokens) => {
  const configs = {
    // Ignição
    ignitionOn: {
      icon: <PowerIcon fontSize="small" />,
      color: tokens.status?.online || '#3fb950',
      label: 'eventIgnitionOn',
    },
    ignitionOff: {
      icon: <PowerOffIcon fontSize="small" />,
      color: tokens.status?.offline || '#f85149',
      label: 'eventIgnitionOff',
    },

    // Movimento
    deviceMoving: {
      icon: <DirectionsCarIcon fontSize="small" />,
      color: tokens.status?.moving || '#58a6ff',
      label: 'eventDeviceMoving',
    },
    deviceStopped: {
      icon: <StopIcon fontSize="small" />,
      color: tokens.status?.stopped || '#8b949e',
      label: 'eventDeviceStopped',
    },

    // Geofences
    geofenceEnter: {
      icon: <LoginIcon fontSize="small" />,
      color: tokens.semantic?.info || '#58a6ff',
      label: 'eventGeofenceEnter',
    },
    geofenceExit: {
      icon: <LogoutIcon fontSize="small" />,
      color: tokens.semantic?.warning || '#d29922',
      label: 'eventGeofenceExit',
    },

    // Alarmes
    alarm: {
      icon: <WarningIcon fontSize="small" />,
      color: tokens.status?.alarm || '#f85149',
      label: 'eventAlarm',
    },
    sos: {
      icon: <WarningIcon fontSize="small" />,
      color: tokens.status?.alarm || '#f85149',
      label: 'eventAlarm',
    },

    // Velocidade
    deviceOverspeed: {
      icon: <SpeedIcon fontSize="small" />,
      color: tokens.semantic?.error || '#f85149',
      label: 'eventDeviceOverspeed',
    },

    // Manutenção
    maintenance: {
      icon: <BuildIcon fontSize="small" />,
      color: tokens.status?.maintenance || '#a371f7',
      label: 'eventMaintenance',
    },

    // Combustível
    deviceFuelDrop: {
      icon: <LocalGasStationIcon fontSize="small" />,
      color: tokens.semantic?.error || '#f85149',
      label: 'eventDeviceFuelDrop',
    },
    deviceFuelIncrease: {
      icon: <LocalGasStationIcon fontSize="small" />,
      color: tokens.semantic?.success || '#3fb950',
      label: 'eventDeviceFuelIncrease',
    },

    // Status do dispositivo
    deviceOnline: {
      icon: <WifiIcon fontSize="small" />,
      color: tokens.status?.online || '#3fb950',
      label: 'eventDeviceOnline',
    },
    deviceOffline: {
      icon: <WifiOffIcon fontSize="small" />,
      color: tokens.status?.offline || '#f85149',
      label: 'eventDeviceOffline',
    },
    deviceUnknown: {
      icon: <GpsFixedIcon fontSize="small" />,
      color: tokens.status?.unknown || '#8b949e',
      label: 'eventDeviceUnknown',
    },

    // Bateria
    lowBattery: {
      icon: <BatteryAlertIcon fontSize="small" />,
      color: tokens.semantic?.warning || '#d29922',
      label: 'eventLowBattery',
    },

    // Notificação genérica
    notification: {
      icon: <NotificationsIcon fontSize="small" />,
      color: tokens.accent?.primary || '#00d4aa',
      label: 'sharedNotification',
    },

    // Default
    default: {
      icon: <LocationOnIcon fontSize="small" />,
      color: tokens.text?.secondary || '#8b949e',
      label: 'eventUnknown',
    },
  };

  return configs[type] || configs.default;
};

// ============================================================================
// COMPONENTE: ActivityEventIcon
// ============================================================================

export const ActivityEventIcon = ({ type, size = 'md', tokens: tokensProp }) => {
  const tokensHook = usePremiumTokens();
  const tokens = tokensProp || tokensHook;
  const config = getEventConfig(type, tokens);

  const sizes = {
    sm: { container: 24, icon: 14 },
    md: { container: 32, icon: 18 },
    lg: { container: 40, icon: 22 },
  };

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <Box
      sx={{
        width: sizeConfig.container,
        height: sizeConfig.container,
        borderRadius: '50%',
        backgroundColor: `${config.color}20`,
        border: `2px solid ${config.color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& .MuiSvgIcon-root': {
          fontSize: sizeConfig.icon,
          color: config.color,
        },
      }}
    >
      {config.icon}
    </Box>
  );
};

ActivityEventIcon.propTypes = {
  type: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  tokens: PropTypes.object,
};

// ============================================================================
// COMPONENTE: ActivityDaySeparator
// ============================================================================

export const ActivityDaySeparator = ({ date, tokens: tokensProp }) => {
  const tokensHook = usePremiumTokens();
  const tokens = tokensProp || tokensHook;
  const t = useTranslation();

  const label = formatDayLabel(date, t);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        my: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: 1,
          backgroundColor: tokens.activityFeed?.daySeparatorBg || tokens.border?.subtle || 'rgba(255,255,255,0.1)',
        }}
      />
      <Typography
        sx={{
          fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
          fontWeight: 600,
          color: tokens.activityFeed?.daySeparatorText || tokens.text?.muted || '#6e7681',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          px: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 1,
          backgroundColor: tokens.activityFeed?.daySeparatorBg || tokens.border?.subtle || 'rgba(255,255,255,0.1)',
        }}
      />
    </Box>
  );
};

ActivityDaySeparator.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  tokens: PropTypes.object,
};

// ============================================================================
// COMPONENTE: ActivityEvent
// ============================================================================

export const ActivityEvent = ({
  event,
  onClick,
  showDevice = true,
  highlighted = false,
  showTimeline = true,
  isLast = false,
  tokens: tokensProp,
}) => {
  const tokensHook = usePremiumTokens();
  const tokens = tokensProp || tokensHook;
  const t = useTranslation();

  const config = getEventConfig(event.type, tokens);
  const relativeTime = formatRelativeTime(event.serverTime || event.eventTime, t);
  const fullTime = new Date(event.serverTime || event.eventTime).toLocaleString();

  // Construir descrição do evento
  const getEventDescription = () => {
    const deviceName = event.deviceName || `Device ${event.deviceId}`;
    const eventLabel = t(config.label) || event.type;

    let description = `${showDevice ? deviceName + ' - ' : ''}${eventLabel}`;

    // Adicionar detalhes específicos
    if (event.geofenceName) {
      description += ` "${event.geofenceName}"`;
    }
    if (event.attributes?.speed) {
      description += ` (${Math.round(event.attributes.speed)} km/h)`;
    }
    if (event.attributes?.alarm) {
      description += ` - ${event.attributes.alarm}`;
    }

    return description;
  };

  return (
    <Box
      onClick={() => onClick?.(event)}
      sx={{
        display: 'flex',
        gap: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        py: 1,
        px: 1,
        mx: -1,
        borderRadius: tokens.radius?.md || '6px',
        transition: tokens.transitions?.colors || 'all 0.15s ease',
        backgroundColor: highlighted
          ? (tokens.activityFeed?.eventBgHover || 'rgba(255,255,255,0.05)')
          : 'transparent',
        '&:hover': onClick ? {
          backgroundColor: tokens.activityFeed?.eventBgHover || 'rgba(255,255,255,0.05)',
        } : {},
      }}
    >
      {/* Timeline Line */}
      {showTimeline && (
        <Box
          sx={{
            position: 'absolute',
            left: 15,
            top: 40,
            bottom: isLast ? '50%' : -8,
            width: 2,
            backgroundColor: tokens.activityFeed?.lineBg || tokens.border?.subtle || 'rgba(255,255,255,0.1)',
          }}
        />
      )}

      {/* Icon */}
      <ActivityEventIcon type={event.type} tokens={tokens} />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
            fontWeight: 500,
            color: tokens.activityFeed?.eventTitle || tokens.text?.primary || '#f0f6fc',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {getEventDescription()}
        </Typography>

        <Tooltip title={fullTime} placement="bottom-start">
          <Typography
            sx={{
              fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
              color: tokens.activityFeed?.eventTime || tokens.text?.muted || '#6e7681',
              mt: 0.25,
              cursor: 'help',
            }}
          >
            {relativeTime}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

ActivityEvent.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired,
    deviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deviceName: PropTypes.string,
    geofenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    geofenceName: PropTypes.string,
    serverTime: PropTypes.string,
    eventTime: PropTypes.string,
    attributes: PropTypes.object,
  }).isRequired,
  onClick: PropTypes.func,
  showDevice: PropTypes.bool,
  highlighted: PropTypes.bool,
  showTimeline: PropTypes.bool,
  isLast: PropTypes.bool,
  tokens: PropTypes.object,
};

// ============================================================================
// COMPONENTE: ActivityFeedSkeleton
// ============================================================================

const ActivityFeedSkeleton = ({ count = 5, tokens }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Skeleton
          variant="circular"
          width={32}
          height={32}
          sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)', flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="text"
            width="80%"
            sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }}
          />
          <Skeleton
            variant="text"
            width="40%"
            sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

// ============================================================================
// COMPONENTE: ActivityFeedEmpty
// ============================================================================

const ActivityFeedEmpty = ({ message, tokens, t }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 6,
      gap: 1,
    }}
  >
    <NotificationsIcon
      sx={{
        fontSize: 48,
        color: tokens.text?.muted || '#6e7681',
        opacity: 0.5,
      }}
    />
    <Typography
      sx={{
        color: tokens.text?.muted || '#6e7681',
        fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
      }}
    >
      {message || t('activityNoEvents') || 'Nenhum evento recente'}
    </Typography>
  </Box>
);

// ============================================================================
// COMPONENTE PRINCIPAL: PremiumActivityFeed
// ============================================================================

const PremiumActivityFeed = ({
  events = [],
  loading = false,
  onEventClick,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  groupByDay = true,
  showDevice = true,
  maxHeight,
  emptyMessage,
  highlightedEventId,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const containerRef = useRef(null);

  // Agrupar eventos por dia
  const groupedEvents = useMemo(() => {
    if (!groupByDay || !events.length) {
      return [{ date: null, events }];
    }

    const groups = [];
    let currentGroup = null;

    events.forEach((event) => {
      const eventDate = new Date(event.serverTime || event.eventTime);

      if (!currentGroup || !isSameDay(currentGroup.date, eventDate)) {
        currentGroup = { date: eventDate, events: [] };
        groups.push(currentGroup);
      }

      currentGroup.events.push(event);
    });

    return groups;
  }, [events, groupByDay]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasMore || loadingMore) return;

    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom) {
      onLoadMore();
    }
  }, [onLoadMore, hasMore, loadingMore]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onLoadMore) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, onLoadMore]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <ActivityFeedSkeleton count={5} tokens={tokens} />
      </Box>
    );
  }

  // Empty state
  if (!events.length) {
    return <ActivityFeedEmpty message={emptyMessage} tokens={tokens} t={t} />;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        maxHeight: maxHeight || 'auto',
        overflow: maxHeight ? 'auto' : 'visible',
        // Custom scrollbar
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: tokens.border?.default || 'rgba(255,255,255,0.2)',
          borderRadius: 3,
          '&:hover': {
            backgroundColor: tokens.border?.strong || 'rgba(255,255,255,0.3)',
          },
        },
      }}
    >
      {groupedEvents.map((group, groupIndex) => (
        <Box key={groupIndex}>
          {/* Day Separator */}
          {group.date && groupByDay && (
            <ActivityDaySeparator date={group.date} tokens={tokens} />
          )}

          {/* Events */}
          {group.events.map((event, eventIndex) => (
            <ActivityEvent
              key={event.id || `${event.deviceId}-${event.serverTime}-${eventIndex}`}
              event={event}
              onClick={onEventClick}
              showDevice={showDevice}
              highlighted={highlightedEventId === event.id}
              showTimeline={true}
              isLast={
                groupIndex === groupedEvents.length - 1 &&
                eventIndex === group.events.length - 1
              }
              tokens={tokens}
            />
          ))}
        </Box>
      ))}

      {/* Loading More Indicator */}
      {loadingMore && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 2,
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: tokens.accent?.primary || '#00d4aa' }}
          />
        </Box>
      )}

      {/* Load More Button (se não usar infinite scroll) */}
      {hasMore && !loadingMore && onLoadMore && (
        <Box
          onClick={onLoadMore}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            cursor: 'pointer',
            color: tokens.accent?.primary || '#00d4aa',
            fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {t('activityLoadMore') || 'Carregar mais'}
        </Box>
      )}
    </Box>
  );
};

PremiumActivityFeed.propTypes = {
  events: PropTypes.array,
  loading: PropTypes.bool,
  onEventClick: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  groupByDay: PropTypes.bool,
  showDevice: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  emptyMessage: PropTypes.string,
  highlightedEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ============================================================================
// HOOK: useActivityFeed
// ============================================================================

export const useActivityFeed = (initialEvents = []) => {
  const [events, setEvents] = React.useState(initialEvents);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);

  const addEvent = useCallback((event) => {
    setEvents((prev) => [event, ...prev]);
  }, []);

  const addEvents = useCallback((newEvents) => {
    setEvents((prev) => [...prev, ...newEvents]);
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const loadMore = useCallback(async (fetchFn) => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const newEvents = await fetchFn();
      if (newEvents.length === 0) {
        setHasMore(false);
      } else {
        addEvents(newEvents);
      }
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, addEvents]);

  return {
    events,
    setEvents,
    loading,
    setLoading,
    hasMore,
    setHasMore,
    addEvent,
    addEvents,
    clearEvents,
    loadMore,
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default PremiumActivityFeed;
